/**
 * Fraction Values Preprocessor
 *
 * Combines fraction compound keys (x__num, x__den) into "num/den" strings
 * before values reach the validators. Also fills in fixed numerator/denominator
 * values from the widget config.
 *
 * This runs before both simple and expression validation.
 */
import type { ExerciseJSON } from '../transformers/json-schema';
import { JSON_TYPES } from '../constants';
import { getFractionKey, isFractionKey } from '../utils/fraction-keys';

type InputValue = string | number | string[][] | number[];

export function preprocessFractionValues(
  exercise: ExerciseJSON,
  userInputs: Record<string, InputValue>
): Record<string, InputValue> {
  const result: Record<string, InputValue> = { ...userInputs };

  for (const [widgetId, widget] of Object.entries(exercise.widgets)) {
    if (widget.type !== JSON_TYPES.FRACTION) continue;

    const numKey = getFractionKey(widgetId, 'num');
    const denKey = getFractionKey(widgetId, 'den');

    const rawNum = result[numKey];
    const rawDen = result[denKey];

    const numEffective = widget.numerator !== null
      ? String(widget.numerator)
      : (typeof rawNum === 'string' || typeof rawNum === 'number' ? String(rawNum) : '');
    const denEffective = widget.denominator !== null
      ? String(widget.denominator)
      : (typeof rawDen === 'string' || typeof rawDen === 'number' ? String(rawDen) : '');

    if (numEffective !== '' && denEffective !== '') {
      result[widgetId] = `${numEffective}/${denEffective}`;
    }

    delete result[numKey];
    delete result[denKey];
  }

  for (const key of Object.keys(result)) {
    if (isFractionKey(key)) {
      delete result[key];
    }
  }

  return result;
}
